import {
  GatewayAvancement,
  GatewayGarantieFinanciere,
} from "@conformite/gateway";
import {
  Form,
  InputButtonGroup,
  InputCheckbox,
  InputNumber,
  useFormConfig,
  FormField,
  FormGrid,
  FormRow,
  displayErrorToast,
  InfoCard,
} from "@endya-conformite/ui-kit";
import { GatewayGarantieFinanciereApi } from "@src/api/conformite/garantieFinanciere.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useOnce } from "@src/hooks/useOnce";
import { useMe } from "@src/store/store.me";
import { chain } from "lodash";
import { useEffect } from "react";
import styles from "../ConformiteGarantieFinanciere.module.scss";
import {
  ConformiteGarantieFinanciereEditFiles,
  FormWithFiles,
} from "./ConformiteGarantieFinanciereEditFiles";
import {
  ConformiteGarantieFinanciereEditFilesNonDiffusable,
  FormWithFileNonDiffusable,
} from "./ConformiteGarantieFinanciereEditFilesNonDiffusable";
import {
  GarantieFinanciereIobspFormValues,
  useGarantieFinanciereSchema,
} from "./hooks/useGarantieFinanciereSchema";

const buttonGroupOptionsBoolean = [
  {
    label: "Oui",
    value: true,
  },
  {
    label: "Non",
    value: false,
  },
];

const getFileNonDiffusableWithoutFile = (
  file: {
    isValid: boolean;
    pathname?: string | undefined;
    filename?: string | undefined;
    file?: File | undefined;
  } | null
): GatewayGarantieFinanciere.ConformiteGarantieFinanciereFileNonDiffusable | null => {
  if (!file) return null;
  const { file: fileData, ...rest } = file;
  return rest;
};

const getHasGarantieFinanciereValue = (
  data: Record<string, unknown>,
  millesime: number
): boolean | null => {
  if (millesime < 2026) return null;
  return "hasGarantieFinanciere" in data
    ? (data.hasGarantieFinanciere as boolean | null)
    : null;
};

const getDate = (date: Date | undefined) => {
  if (!date) return new Date();
  return new Date(date);
};

export const useConformiteGarantieFinanciereIobspEdit = (
  siren: string,
  millesime: number,
  garantieFinanciere: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIobsp | null,
  stopEditing: (
    value:
      GatewayGarantieFinanciere.ConformiteGarantieFinanciereIobsp | undefined,
    avancement: GatewayAvancement.AvancementSociete
  ) => void
) => {
  const schema = useGarantieFinanciereSchema(millesime, "iobsp");

  const form = useFormConfig({
    schema,
    defaultValues: {
      millesime,
      hasGarantieFinanciere: garantieFinanciere?.hasGarantieFinanciere ?? false,
      hasExpertComptable: garantieFinanciere?.hasExpertComptable ?? false,
      encaissementFonds: garantieFinanciere?.encaissementFonds ?? false,
      nonDiffusable: {
        noCommunication: garantieFinanciere?.noCommunication ?? false,
        fileNonDiffusable: garantieFinanciere?.fileNonDiffusable ?? null,
      },
      files:
        garantieFinanciere?.files?.map((file) => ({
          ...file,
          startDate: getDate(file.startDate),
          endDate: getDate(file.endDate),
        })) ?? [],
      infoGarantie: {
        primesEncaisseesDehorsMandat:
          garantieFinanciere?.primesEncaisseesDehorsMandat ?? 0,
        primesEncaisseesMontant:
          garantieFinanciere?.primesEncaisseesMontant ?? 0,
        indemniteSinistreDehorsMandat:
          garantieFinanciere?.indemniteSinistreDehorsMandat ?? 0,
        indemniteSinistreMontant:
          garantieFinanciere?.indemniteSinistreMontant ?? 0,
        montantGarantie: garantieFinanciere?.montantGarantie ?? 0,
        montantFondRoulement: garantieFinanciere?.montantFondRoulement ?? 0,
      },
    },
  });
  const onSubmit = async ({
    files = [],
    nonDiffusable,
    infoGarantie = {
      primesEncaisseesDehorsMandat: 0,
      primesEncaisseesMontant: 0,
      indemniteSinistreDehorsMandat: 0,
      indemniteSinistreMontant: 0,
      montantGarantie: 0,
      montantFondRoulement: 0,
    },
    ...data
  }: GarantieFinanciereIobspFormValues) => {
    const filesToSend = chain(files)
      .filter((file) => !!file?.file)
      .map((file) => file?.file)
      .value() as File[];
    const filesWithoutBlob = files.map((fileData) => {
      if (!fileData?.file) return fileData;
      const { file, ...rest } = fileData;
      return rest;
    });
    const payload: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIobsp =
      {
        noCommunication: nonDiffusable.noCommunication,
        millesime: data.millesime,
        hasGarantieFinanciere: getHasGarantieFinanciereValue(data, millesime),
        montantGarantie: infoGarantie.montantGarantie ?? null,
        encaissementFonds: data.encaissementFonds,
        montantFondRoulement: infoGarantie.montantFondRoulement ?? null,
        primesEncaisseesMontant: infoGarantie.primesEncaisseesMontant ?? null,
        indemniteSinistreMontant: infoGarantie.indemniteSinistreMontant ?? null,
        primesEncaisseesDehorsMandat:
          infoGarantie.primesEncaisseesDehorsMandat ?? null,
        indemniteSinistreDehorsMandat:
          infoGarantie.indemniteSinistreDehorsMandat ?? null,
        files: filesWithoutBlob,
        fileNonDiffusable: getFileNonDiffusableWithoutFile(
          nonDiffusable.fileNonDiffusable
        ),
        hasExpertComptable: data.hasExpertComptable,
      };
    try {
      const avancement =
        await GatewayGarantieFinanciereApi.saveGarantieFinanciereIobsp(
          siren,
          millesime,
          payload,
          filesToSend.concat(
            nonDiffusable.fileNonDiffusable?.file
              ? [nonDiffusable.fileNonDiffusable?.file]
              : []
          )
        );
      stopEditing(payload, avancement);
    } catch (err) {
      handleGatewayError({
        onUnhandled: () => {
          displayErrorToast(
            "Une erreur est survenue lors de la récupération du formulaire"
          );
        },
      })(err);
    }
  };
  return {
    form,
    onSubmit,
  };
};

type ConformiteGarantieFinanciereEditFormIobspProps = {
  form: ReturnType<typeof useConformiteGarantieFinanciereIobspEdit>["form"];
  onSubmit?: ReturnType<
    typeof useConformiteGarantieFinanciereIobspEdit
  >["onSubmit"];
  millesime: number;
  garantieFinanciere: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIobsp | null;
  disabled?: boolean;
  siren: string;
};

export function ConformiteGarantieFinanciereEditFormIobsp({
  form,
  onSubmit = () => Promise.resolve(),
  millesime,
  garantieFinanciere,
  disabled,
  siren,
}: ConformiteGarantieFinanciereEditFormIobspProps) {
  const { me } = useMe();

  const [
    encaissementFonds,
    indemniteSinistreDehorsMandat,
    primesEncaisseesDehorsMandat,
    noCommunication,
    montantFondRoulement,
  ] = form.watch([
    "encaissementFonds",
    "infoGarantie.indemniteSinistreDehorsMandat",
    "infoGarantie.primesEncaisseesDehorsMandat",
    "nonDiffusable.noCommunication",
    "infoGarantie.montantFondRoulement",
  ]);

  const hasGarantieFinanciere =
    millesime >= 2026 ? form.watch("hasGarantieFinanciere") : null;

  const montantCalculeGarantieFinanciere = Math.max(
    ((indemniteSinistreDehorsMandat || 0) +
      (primesEncaisseesDehorsMandat || 0)) /
      6 +
      (montantFondRoulement || 0),
    115_000
  ).toFixed(2);

  useOnce(() => {
    if (!disabled && garantieFinanciere !== null) {
      form.trigger().catch(console.error);
    }
  });

  const fieldTriggeringMontantGarantie = form.watch([
    "infoGarantie.primesEncaisseesDehorsMandat",
    "infoGarantie.primesEncaisseesMontant",
    "infoGarantie.indemniteSinistreDehorsMandat",
    "infoGarantie.indemniteSinistreMontant",
  ]);

  useEffect(() => {
    form.trigger("infoGarantie.montantGarantie").catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldTriggeringMontantGarantie, form.trigger]);

  return (
    <Form formConfig={form} onSubmit={onSubmit}>
      <fieldset disabled={disabled} className={styles.fieldset}>
        <FormGrid disableHorizontalGutter>
          {millesime >= 2026 && (
            <FormRow>
              <FormField>
                <InputButtonGroup<
                  GarantieFinanciereIobspFormValues,
                  false,
                  boolean
                >
                  label="Disposez-vous d'une garantie financière ?"
                  name="hasGarantieFinanciere"
                  variant="outline-pop"
                  options={buttonGroupOptionsBoolean}
                  disabled={disabled}
                />
              </FormField>
            </FormRow>
          )}
          <FormRow>
            <FormField>
              <InputButtonGroup<
                GarantieFinanciereIobspFormValues,
                false,
                boolean
              >
                label="Avez vous un expert comptable ?"
                name="hasExpertComptable"
                variant="outline-pop"
                options={buttonGroupOptionsBoolean}
                disabled={disabled}
              />
            </FormField>
          </FormRow>
          {(millesime < 2026 || hasGarantieFinanciere === true) && (
            <FormRow>
              <FormField>
                <InputButtonGroup<
                  GarantieFinanciereIobspFormValues,
                  false,
                  boolean
                >
                  label="Avez-vous encaissé des fonds lors des douze derniers mois ?"
                  name="encaissementFonds"
                  variant="outline-pop"
                  options={buttonGroupOptionsBoolean}
                  disabled={disabled}
                />
              </FormField>
            </FormRow>
          )}
          {(millesime < 2026 || hasGarantieFinanciere === true) &&
            encaissementFonds && (
              <>
                <FormRow>
                  <FormField>
                    <InputNumber<GarantieFinanciereIobspFormValues>
                      name="infoGarantie.montantGarantie"
                      label="Quel est le montant à date de votre garantie financière ?"
                    />
                  </FormField>
                </FormRow>
                <h4 className={styles.subtitle}>
                  Attestation de couverture sur l&apos;année {millesime}
                </h4>
                {me?.entreprisePrincipale.millesimeActuel === 2024 && (
                  <InfoCard className={styles.cardInformationsPJ}>
                    Dans le cadre du renouvellement 2024, si vous ne disposez
                    pas encore de votre attestation pour l&apos;année 2024/2025,
                    nous vous invitons à renseigner et enregistrer les éléments
                    dont vous disposez à ce jour. Vous pourrez compléter cet axe
                    à réception de votre attestation.
                  </InfoCard>
                )}
                <ConformiteGarantieFinanciereEditFiles
                  form={form as unknown as FormWithFiles}
                  disabled={disabled}
                  siren={siren}
                />
                {!noCommunication && (
                  <>
                    <h4 className={styles.subtitle}>
                      Primes encaissées en {millesime - 1}
                    </h4>
                    <FormRow>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIobspFormValues>
                          name="infoGarantie.primesEncaisseesDehorsMandat"
                          label="En dehors de tout mandat exprès de l'assureur (en €)"
                        />
                      </FormField>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIobspFormValues>
                          name="infoGarantie.primesEncaisseesMontant"
                          label="Dans le cadre d'un mandat exprès de l'assureur (en €)"
                        />
                      </FormField>
                    </FormRow>
                    <h4 className={styles.subtitle}>
                      Indemnités sinistres encaissées en {millesime - 1}
                    </h4>
                    <FormRow>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIobspFormValues>
                          name="infoGarantie.indemniteSinistreDehorsMandat"
                          label="En dehors de tout mandat exprès de l'assureur (en €)"
                        />
                      </FormField>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIobspFormValues>
                          name="infoGarantie.indemniteSinistreMontant"
                          label="Dans le cadre d'un mandat exprès de l'assureur (en €)"
                        />
                      </FormField>
                    </FormRow>
                  </>
                )}
                <FormRow>
                  <FormField>
                    <InputCheckbox<GarantieFinanciereIobspFormValues>
                      name="nonDiffusable.noCommunication"
                      label="Ne souhaite pas communiquer sur les primes et indemnités encaissées"
                    />
                  </FormField>
                </FormRow>
                {noCommunication && (
                  <>
                    {!disabled && (
                      <InfoCard
                        type="warning"
                        className={styles.noCommunicationWarning}
                      >
                        Attention : Si vous ne souhaitez pas communiquer sur les
                        primes et indemnités encaissées, vous devez fournir une
                        attestation de la part de votre assureur, garantissant
                        que la société est suffisamment assurée. La validation
                        de cette pièce nécessitant un traitement manuel, le
                        délai de réception de l&apos;attestation de conformité
                        s&apos;en trouvera allongé.
                      </InfoCard>
                    )}
                    <ConformiteGarantieFinanciereEditFilesNonDiffusable
                      form={form as unknown as FormWithFileNonDiffusable}
                      disabled={disabled}
                      siren={siren}
                      millesime={millesime}
                    />
                  </>
                )}
                {!noCommunication && (
                  <div className={styles.calculatedContainer}>
                    <p className={styles.calculatedLabel}>
                      Montant calculé de la garantie financière :
                    </p>
                    <p className={styles.calculatedValue}>
                      {montantCalculeGarantieFinanciere} €
                    </p>
                  </div>
                )}
                <FormRow>
                  <FormField>
                    <InputNumber<GarantieFinanciereIobspFormValues>
                      name="infoGarantie.montantFondRoulement"
                      label="Montant du fond de roulement (en €)"
                    />
                  </FormField>
                </FormRow>
              </>
            )}
        </FormGrid>
      </fieldset>
    </Form>
  );
}
